import {
  CoogoExecutionError,
  type CoogoExecutionLease,
  type CoogoExecutionLeaseController,
} from "@cogo/webmcp-runtime";
import {
  requestStudioExecutionLease,
  requestStudioExecutionLeaseRelease,
  requestStudioExecutionLeaseRenewal,
  requestStudioOrchestrationResumeGrant,
} from "./studio-orchestration-journal-api";

export const STUDIO_EXECUTION_LEASE_TTL_MS = 30_000;
export const STUDIO_EXECUTION_LEASE_RENEWAL_MS = 10_000;
export const STUDIO_EXECUTION_LEASE_EXPIRY_MARGIN_MS = 5_000;

function executionLeaseLost(cause: unknown): CoogoExecutionError {
  if (
    cause instanceof CoogoExecutionError &&
    cause.code === "execution_lease_lost"
  ) {
    return cause;
  }
  return new CoogoExecutionError(
    `Execution lease lost: ${cause instanceof Error ? cause.message : String(cause)}`,
    "execution_lease_lost",
  );
}

function assertUsableLease(lease: CoogoExecutionLease, now: number): void {
  const expiresAt = Date.parse(lease.expiresAt);
  if (
    !lease.leaseId.trim() ||
    !Number.isInteger(lease.fencingToken) ||
    lease.fencingToken <= 0 ||
    !Number.isFinite(expiresAt) ||
    expiresAt - now <= STUDIO_EXECUTION_LEASE_EXPIRY_MARGIN_MS
  ) {
    throw new CoogoExecutionError(
      "Execution lease expires inside the required five-second margin.",
      "execution_lease_lost",
    );
  }
}

export function createStudioCoogoExecutionLeaseController(input: {
  projectId: string;
  runId: string;
  clientInstanceId: string;
  getCheckpointRevision: () => number;
  now?: () => number;
  onLeaseLost?: (error: CoogoExecutionError) => void;
}): CoogoExecutionLeaseController {
  const now = input.now ?? Date.now;
  let activeLease: CoogoExecutionLease | undefined;
  let releasedLeaseId: string | undefined;

  const authorityInput = (lease: CoogoExecutionLease) => ({
    projectId: input.projectId,
    runId: input.runId,
    leaseId: lease.leaseId,
    fencingToken: lease.fencingToken,
    clientInstanceId: input.clientInstanceId,
  });

  const lose = (cause: unknown): never => {
    const error = executionLeaseLost(cause);
    input.onLeaseLost?.(error);
    throw error;
  };

  return {
    async acquire({ plan, snapshot, signal }) {
      if (signal.aborted) throw signal.reason;
      if (activeLease && releasedLeaseId !== activeLease.leaseId) {
        throw new CoogoExecutionError(
          "This Studio client already holds an execution lease for the run.",
          "execution_lease_active",
        );
      }
      if (
        plan.runId !== input.runId ||
        plan.projectId !== input.projectId ||
        snapshot.runId !== input.runId ||
        snapshot.projectId !== input.projectId
      ) {
        throw new CoogoExecutionError(
          "Resume lease scope does not match the configured durable run.",
          "resume_grant_binding_mismatch",
        );
      }
      const binding = snapshot.admissionBinding;
      if (
        snapshot.status !== "suspended" ||
        !binding ||
        binding.durability !== "durable"
      ) {
        throw new CoogoExecutionError(
          "Only a durable suspended human gate may acquire a resume lease.",
          "resume_grant_binding_mismatch",
        );
      }
      const expectedCheckpointRevision = input.getCheckpointRevision();
      const grant = await requestStudioOrchestrationResumeGrant({
        projectId: input.projectId,
        runId: input.runId,
        gateNodeId: binding.gateNodeId,
        confirmationId: binding.confirmationId,
        target: structuredClone(binding.target),
        expectedCheckpointRevision,
      });
      if (grant.checkpointRevision !== expectedCheckpointRevision) {
        throw new CoogoExecutionError(
          "Resume grant acknowledged a different checkpoint revision.",
          "resume_grant_checkpoint_mismatch",
        );
      }
      if (signal.aborted) throw signal.reason;
      const acquired = await requestStudioExecutionLease({
        projectId: input.projectId,
        runId: input.runId,
        grantToken: grant.grantToken,
        clientInstanceId: input.clientInstanceId,
      }).catch(lose);
      if (acquired.checkpointRevision !== expectedCheckpointRevision) {
        return lose(
          new Error(
            "Execution lease acknowledged a different checkpoint revision.",
          ),
        );
      }
      const lease: CoogoExecutionLease = {
        leaseId: acquired.leaseId,
        fencingToken: acquired.fencingToken,
        expiresAt: acquired.expiresAt,
      };
      try {
        assertUsableLease(lease, now());
      } catch (error) {
        return lose(error);
      }
      activeLease = lease;
      releasedLeaseId = undefined;
      return lease;
    },

    async renew(lease, signal) {
      if (signal.aborted) throw signal.reason;
      if (
        !activeLease ||
        activeLease.leaseId !== lease.leaseId ||
        activeLease.fencingToken !== lease.fencingToken ||
        releasedLeaseId === lease.leaseId
      ) {
        return lose(
          new Error("Execution lease is not the active Studio lease."),
        );
      }
      try {
        const renewed = await requestStudioExecutionLeaseRenewal(
          authorityInput(lease),
        );
        if (
          renewed.leaseId !== lease.leaseId ||
          renewed.fencingToken !== lease.fencingToken
        ) {
          return lose(
            new Error("Execution lease renewal did not preserve authority."),
          );
        }
        assertUsableLease(renewed, now());
        Object.assign(lease, renewed);
        activeLease = lease;
        return lease;
      } catch (error) {
        return lose(error);
      }
    },

    async assertCurrent(lease, signal) {
      // The reviewed Platform protocol deliberately has no separate assertion
      // route. lease-renew is the authenticated holder/fence assertion and also
      // refreshes the 30-second server TTL. The executor still owns its fixed
      // independent ten-second renewal cadence.
      await this.renew(lease, signal);
    },

    async release(lease) {
      if (releasedLeaseId === lease.leaseId) return;
      if (
        !activeLease ||
        activeLease.leaseId !== lease.leaseId ||
        activeLease.fencingToken !== lease.fencingToken
      ) {
        return lose(
          new Error("Execution lease is not the active Studio lease."),
        );
      }
      try {
        await requestStudioExecutionLeaseRelease(authorityInput(lease));
        releasedLeaseId = lease.leaseId;
      } catch (error) {
        return lose(error);
      }
    },
  };
}
